<!--
  拉伸属性栏组件。
  文件定位：
  - 该组件用于 PropertyManager 中的“拉伸凸台/基体”属性编辑；
  - 它只负责把拉伸状态映射为 SolidWorks 风格的属性栏，不承担几何计算；
  - 所有参数更新都通过 PropertyManagerContext 回调回到 useExtrudeFeature，再统一进入 cad-core 命令链。

  组件职责：
  - 展示方向 1、方向 2、薄壁特征、所选轮廓四个分组；
  - 使用 PuppyCollapseItem、PuppyFormItem、PuppyInputSelect 统一属性栏交互风格；
  - 把单位、启用态、禁用态、列表选择态都收口在同一个视图组件内。
-->
<template>
  <div class="extrude-property" data-testid="extrude-property-section">
    <n-collapse :default-expanded-names="defaultExpanded" class="pm-collapse">
      <PuppyCollapseItem title="方向 1" name="direction1">
        <div class="section-grid">
          <PuppyFormItem>
            <template #icon>
              <span class="field-label-text">条件</span>
            </template>
            <n-select
              :value="direction1.endCondition"
              size="small"
              :options="isCutOperation ? endConditionOptions.slice(0, 1) : availableEndConditionOptions"
              @update:value="handleDirection1EndConditionUpdate"
            />
          </PuppyFormItem>

          <PuppyFormItem v-if="direction1.endCondition !== 'mid_plane'">
            <template #icon>
              <span class="field-label-empty" aria-hidden="true"></span>
            </template>
            <n-checkbox
              v-if="!isCutOperation"
              :checked="direction1.reverse"
              @update:checked="handleDirection1ReverseUpdate"
            >
              反向
            </n-checkbox>
            <n-checkbox
              v-else
              :checked="cutOptions?.flipSideToCut ?? false"
              @update:checked="handleCutFlipSideToCutUpdate"
            >
              反侧切除
            </n-checkbox>
          </PuppyFormItem>

          <PuppyFormItem v-if="['blind', 'mid_plane'].includes(direction1.endCondition)">
            <template #icon>
              <span class="field-label-text">厚度</span>
            </template>
            <n-input-group>
              <n-input-number
                :value="formatLengthForPropertyManager(direction1.depth)"
                size="small"
                :min="PROPERTY_MANAGER_LENGTH_MIN_MM"
                :precision="3"
                :step="1"
                @update:value="handleDirection1DepthUpdate"
              />
              <n-input-group-label size="small">mm</n-input-group-label>
            </n-input-group>
          </PuppyFormItem>

          <ExtrudeEndTargetSection :direction="direction1" :active="extrudeState?.activeEndTargetDirection === 'direction1'"
            :item="extrudeState?.endTargetItems?.direction1" @visual="props.context.setExtrudeEndTargetVisual?.('direction1', $event)"
            @update:direction="commitDirection1" @activate="props.context.setExtrudeEndTargetInputActive?.($event ? 'direction1' : null)" />

          <PuppyFormItem
            can-select
            :selected="direction1.draft.enabled"
            @select="toggleDirection1Draft"
          >
            <template #icon>
              <SvgIcon :content="draftIconMarkup" alt="拔模" class="field-label-icon" />
            </template>
            <n-input-group>
              <n-input-number
                :value="direction1.draft.angleDeg"
                size="small"
                :min="0"
                :max="90"
                :precision="2"
                :step="1"
                :disabled="!direction1.draft.enabled"
                @update:value="handleDirection1DraftAngleUpdate"
              />
              <n-input-group-label size="small">度</n-input-group-label>
            </n-input-group>
          </PuppyFormItem>

          <PuppyFormItem>
            <template #icon>
              <span class="field-label-empty" aria-hidden="true"></span>
            </template>
            <n-checkbox
              :checked="direction1.draft.outward"
              :disabled="!direction1.draft.enabled"
              @update:checked="handleDirection1DraftOutwardUpdate"
            >
              向外拔模
            </n-checkbox>
          </PuppyFormItem>
        </div>
      </PuppyCollapseItem>

      <PuppyCollapseItem
        v-if="direction1.endCondition !== 'mid_plane'"
        title="方向 2"
        name="direction2"
        checkbox
        :check-value="direction2.enabled"
        @update:check-value="handleDirection2EnabledUpdate"
      >
        <div class="section-grid">
          <PuppyFormItem>
            <template #icon>
              <span class="field-label-text">条件</span>
            </template>
            <n-select
              :value="direction2.endCondition"
              size="small"
              :options="isCutOperation ? endConditionOptions.slice(0, 1) : availableEndConditionOptions.filter((option) => option.value !== 'mid_plane')"
              @update:value="handleDirection2EndConditionUpdate"
            />
          </PuppyFormItem>

          <PuppyFormItem v-if="direction2.endCondition === 'blind'">
            <template #icon>
              <span class="field-label-text">厚度</span>
            </template>
            <n-input-group>
              <n-input-number
                :value="formatLengthForPropertyManager(direction2.depth)"
                size="small"
                :min="PROPERTY_MANAGER_LENGTH_MIN_MM"
                :precision="3"
                :step="1"
                @update:value="handleDirection2DepthUpdate"
              />
              <n-input-group-label size="small">mm</n-input-group-label>
            </n-input-group>
          </PuppyFormItem>

          <ExtrudeEndTargetSection :direction="direction2" :active="extrudeState?.activeEndTargetDirection === 'direction2'"
            :item="extrudeState?.endTargetItems?.direction2" @visual="props.context.setExtrudeEndTargetVisual?.('direction2', $event)"
            @update:direction="commitDirection2" @activate="props.context.setExtrudeEndTargetInputActive?.($event ? 'direction2' : null)" />

          <PuppyFormItem
            can-select
            :selected="direction2.draft.enabled"
            @select="toggleDirection2Draft"
          >
            <template #icon>
              <SvgIcon :content="draftIconMarkup" alt="拔模" class="field-label-icon" />
            </template>
            <n-input-group>
              <n-input-number
                :value="direction2.draft.angleDeg"
                size="small"
                :min="0"
                :max="90"
                :precision="2"
                :step="1"
                :disabled="!direction2.draft.enabled"
                @update:value="handleDirection2DraftAngleUpdate"
              />
              <n-input-group-label size="small">度</n-input-group-label>
            </n-input-group>
          </PuppyFormItem>

          <PuppyFormItem>
            <template #icon>
              <span class="field-label-empty" aria-hidden="true"></span>
            </template>
            <n-checkbox
              :checked="direction2.draft.outward"
              :disabled="!direction2.draft.enabled"
              @update:checked="handleDirection2DraftOutwardUpdate"
            >
              向外拔模
            </n-checkbox>
          </PuppyFormItem>
        </div>
      </PuppyCollapseItem>

      <PuppyCollapseItem
        title="薄壁特征"
        name="thinFeature"
        checkbox
        :check-value="thinFeature.enabled"
        @update:check-value="handleThinFeatureEnabledUpdate"
      >
        <div class="section-grid">
          <PuppyFormItem>
            <template #icon>
              <SvgIcon :content="thinFeatureIconMarkup" alt="薄壁特征" class="field-label-icon" />
            </template>
            <n-select
              :value="thinFeature.type"
              size="small"
              :options="thinFeatureTypeOptions"
              @update:value="handleThinFeatureTypeUpdate"
            />
          </PuppyFormItem>

          <PuppyFormItem v-if="thinFeature.type === 'one-direction'">
            <template #icon>
              <span class="field-label-empty" aria-hidden="true"></span>
            </template>
            <n-checkbox
              :checked="thinFeature.reverse"
              @update:checked="handleThinFeatureReverseUpdate"
            >
              反向
            </n-checkbox>
          </PuppyFormItem>

          <PuppyFormItem v-if="thinFeature.type !== 'two-direction'">
            <template #icon>
              <span class="field-label-text">厚度</span>
            </template>
            <n-input-group>
              <n-input-number
                :value="formatLengthForPropertyManager(thinFeature.thickness)"
                size="small"
                :min="PROPERTY_MANAGER_LENGTH_MIN_MM"
                :precision="3"
                :step="1"
                @update:value="handleThinFeatureThicknessUpdate"
              />
              <n-input-group-label size="small">mm</n-input-group-label>
            </n-input-group>
          </PuppyFormItem>

          <template v-else>
            <PuppyFormItem>
              <template #icon>
                <span class="field-label-text">厚度 1</span>
              </template>
              <n-input-group>
                <n-input-number
                  :value="formatLengthForPropertyManager(thinFeature.thickness1)"
                  size="small"
                  :min="PROPERTY_MANAGER_LENGTH_MIN_MM"
                  :precision="3"
                  :step="1"
                  @update:value="handleThinFeatureThickness1Update"
                />
                <n-input-group-label>mm</n-input-group-label>
              </n-input-group>
            </PuppyFormItem>

            <PuppyFormItem>
              <template #icon>
                <span class="field-label-text">厚度 2</span>
              </template>
              <n-input-group>
                <n-input-number
                  :value="formatLengthForPropertyManager(thinFeature.thickness2)"
                  size="small"
                  :min="PROPERTY_MANAGER_LENGTH_MIN_MM"
                  :precision="3"
                  :step="1"
                  @update:value="handleThinFeatureThickness2Update"
                />
                <n-input-group-label>mm</n-input-group-label>
              </n-input-group>
            </PuppyFormItem>
          </template>

          <PuppyFormItem v-if="isCutOperation || extrudeState?.canUseCapEnds">
            <template #icon>
              <span class="field-label-empty" aria-hidden="true"></span>
            </template>
            <n-checkbox
              :checked="thinFeature.capEnds"
              @update:checked="handleThinFeatureCapEndsUpdate"
            >
              顶端加盖
            </n-checkbox>
          </PuppyFormItem>

          <PuppyFormItem v-if="thinFeature.capEnds && (isCutOperation || extrudeState?.canUseCapEnds)">
            <template #icon>
              <span class="field-label-text">后端</span>
            </template>
            <n-input-group>
              <n-input-number
                :value="formatLengthForPropertyManager(thinFeature.capThickness)"
                size="small"
                :min="PROPERTY_MANAGER_LENGTH_MIN_MM"
                :precision="3"
                :step="1"
                @update:value="handleThinFeatureCapThicknessUpdate"
              />
              <n-input-group-label size="small">mm</n-input-group-label>
            </n-input-group>
          </PuppyFormItem>
        </div>
      </PuppyCollapseItem>

      <PuppyCollapseItem title="所选轮廓" name="contours">
        <div class="section-grid">
          <PuppyInputSelect
              :items="selectedContours"
              :active="selectedContoursInputActive"
              :active-index="activeContourIndex"
              :selected-indices="selectedContourIndices"
              :visible-rows="3"
              item-key-field="regionId"
              item-label-field="label"
              empty-text="尚未选择轮廓"
              @update:active="handleSelectedContoursInputActiveUpdate"
              @update:active-index="handleActiveContourIndexUpdate"
              @update:selected-indices="handleSelectedContourIndicesUpdate"
              @delete-item="handleDeleteContour"
              @delete-all="handleDeleteAllContours"
              @item-click="handleContourClick"
          >
            <template #item="{ item }">
              <div
                  class="contour-item"
                  @mouseenter="handleHoverContour((item as ExtrudeSelectedContourItem).regionId)"
                  @mouseleave="handleHoverContour(null)"
              >
                <span class="contour-diamond">◇</span>
                <span class="contour-label">{{ (item as ExtrudeSelectedContourItem).label }}</span>
              </div>
            </template>
          </PuppyInputSelect>
        </div>
      </PuppyCollapseItem>
    </n-collapse>
  </div>
</template>

<script setup lang="ts">
/**
 * 拉伸属性栏逻辑层。
 *
 * 设计说明：
 * - 视图层不直接改写嵌套对象，所有更新都通过“复制旧值 -> 覆盖目标字段 -> 调用 context setter”完成；
 * - Direction 2 与薄壁分组都使用 PuppyCollapseItem 的启用态能力；
 * - 所选轮廓通过 PuppyInputSelect 激活后持续接收场景选择。
 */
import { computed, ref, watch } from 'vue';
import {
  NCheckbox,
  NCollapse,
  NInputGroup,
  NInputGroupLabel,
  NInputNumber,
  NSelect,
} from 'naive-ui';
import type {
  ExtrudeCutDefinition,
  ExtrudeDirectionDefinition,
  ExtrudeEndTargetDisplay,
  ExtrudeEndCondition,
  ExtrudeThinFeatureDefinition,
} from '@puppycad/cad-core';
import type {
  ExtrudeSelectedContourItem,
  PropertyManagerContext,
} from '@/core';
import {
  CutExtrudeDefinitionCoordinator,
  convertMToMm,
  convertMmToM,
  ExtrudeFeatureEditDefinitionFactory,
} from '@puppycad/cad-core';
import SvgIcon from '@/components/common/SvgIcon.vue';
import draftIconMarkup from '@/assets/icon/feature_draft.svg?raw';
import thinFeatureIconMarkup from '@/assets/icon/feature_shell.svg?raw';
import PuppyCollapseItem from '@/components/PuppyCollapseItem/PuppyCollapseItem.vue';
import PuppyFormItem from '@/components/PuppyFormItem/PuppyFormItem.vue';
import PuppyInputSelect from '@/components/PuppyInputSelect/PuppyInputSelect.vue';
import ExtrudeEndTargetSection from './ExtrudeEndTargetSection.vue';

interface ExtrudePropertySectionProps {
  context: PropertyManagerContext;
}

const props = defineProps<ExtrudePropertySectionProps>();

const defaultExpanded = ['direction1', 'contours'];
const endConditionOptions = [
  {
    label: '给定深度',
    value: 'blind',
  },
  { label: '完全贯穿', value: 'through_all' },
  { label: '成形到顶点', value: 'up_to_vertex' },
  { label: '成形到面', value: 'up_to_surface' },
  { label: '到离指定面指定的距离', value: 'offset_from_surface' },
  { label: '成形到实体', value: 'up_to_body' },
  { label: '两侧对称', value: 'mid_plane' },
];
const thinFeatureTypeOptions = [
  {
    label: '单向',
    value: 'one-direction',
  },
  {
    label: '两侧对称',
    value: 'mid-plane',
  },
  {
    label: '双向',
    value: 'two-direction',
  },
];

const activeContourIndex = ref(-1);
const selectedContourIndices = ref<number[]>([]);
const fallbackDirection: ExtrudeDirectionDefinition = ExtrudeFeatureEditDefinitionFactory.createDefaultDirection();
const fallbackThinFeature: ExtrudeThinFeatureDefinition = ExtrudeFeatureEditDefinitionFactory.createDefaultThinFeature();
const fallbackCutOptions: ExtrudeCutDefinition = CutExtrudeDefinitionCoordinator.createDefaultCutOptions({
  direction1: fallbackDirection,
  direction2: {
    ...fallbackDirection,
    enabled: false,
  },
});
/** PropertyManager 中长度输入的最小毫米值，避免把 0 写入底层几何。 */
const PROPERTY_MANAGER_LENGTH_MIN_MM = 0.001;

/**
 * 读取当前拉伸状态。
 */
const extrudeState = computed(() => props.context.extrudeState as {
  endTargetItems?: Partial<Record<'direction1' | 'direction2', ExtrudeEndTargetDisplay>>;
  canUseThroughAll?: boolean;
  canUseCapEnds?: boolean;
  operation?: 'boss' | 'cut';
  activeEndTargetDirection?: 'direction1' | 'direction2' | null;
  definition?: {
    featureKind?: 'boss-extrude' | 'cut-extrude';
    direction1?: typeof fallbackDirection;
    direction2?: typeof fallbackDirection;
    thinFeature?: typeof fallbackThinFeature;
    cutOptions?: ExtrudeCutDefinition | null;
    selectedContoursInputActive?: boolean;
  };
  selectedContours?: unknown[];
  previewError?: string | null;
} | null);

/**
 * 读取方向 1。
 */
const direction1 = computed(() => extrudeState.value?.definition?.direction1 ?? fallbackDirection);

/** D1/D2消费同一SDK能力，重编辑时保留失效的既有条件以便用户明确修正。 */
const availableEndConditionOptions = computed(() => endConditionOptions
  .filter((option) => option.value !== 'through_all' || extrudeState.value?.canUseThroughAll === true
    || direction1.value.endCondition === 'through_all' || direction2.value.endCondition === 'through_all')
  .map((option) => option.value === 'through_all' && extrudeState.value?.canUseThroughAll !== true
    ? { ...option, label: '完全贯穿（当前无实体）', disabled: true } : option));

/**
 * 读取方向 2。
 */
const direction2 = computed(() => extrudeState.value?.definition?.direction2 ?? {
  ...fallbackDirection,
  enabled: false,
});

/**
 * 读取当前是否为切除拉伸。
 */
const isCutOperation = computed(() => (
  extrudeState.value?.definition?.featureKind === 'cut-extrude'
  || extrudeState.value?.operation === 'cut'
));

/**
 * 读取薄壁特征。
 */
const thinFeature = computed(() => extrudeState.value?.definition?.thinFeature ?? fallbackThinFeature);

/**
 * 读取切除扩展定义。
 */
const cutOptions = computed(() => extrudeState.value?.definition?.cutOptions ?? fallbackCutOptions);

/**
 * 读取轮廓输入激活态。
 */
const selectedContoursInputActive = computed(() => (
  extrudeState.value?.definition?.selectedContoursInputActive ?? false
));

/**
 * 读取所选轮廓列表。
 */
const selectedContours = computed(() => extrudeState.value?.selectedContours ?? []);

//------ 拉伸属性长度单位转换 开始------
/**
 * 将底层米制长度格式化为 PropertyManager 的毫米显示值。
 *
 * @param value 底层米制长度。
 * @returns 毫米显示值。
 */
function formatLengthForPropertyManager(value: number): number {
  // PropertyManager 面向用户显示毫米，避免把 Three.js 米制值直接暴露给 UI。
  return Number(convertMToMm(value).toFixed(3));
}

/**
 * 将 PropertyManager 的毫米输入归一化为底层米制长度。
 *
 * @param value 用户输入的毫米值。
 * @param fallback 底层米制回退值。
 * @returns 可提交给 cad-core 的米制长度。
 */
function normalizePositiveLengthFromPropertyManager(value: number | null, fallback: number): number {
  if (value === null || Number.isNaN(value) || !Number.isFinite(value)) {
    return fallback;
  }

  // UI 中 90 表示 90mm，写入底层前必须转换为 0.09m。
  return convertMmToM(Math.max(PROPERTY_MANAGER_LENGTH_MIN_MM, Math.abs(value)));
}
//------ 拉伸属性长度单位转换 结束------

/**
 * 裁剪并规范化角度值。
 *
 * @param value 输入值。
 * @param fallback 回退值。
 * @returns 归一化后的角度。
 */
function normalizeAngleValue(value: number | null, fallback: number): number {
  if (value === null || Number.isNaN(value) || !Number.isFinite(value)) {
    return fallback;
  }
  return Math.min(90, Math.max(0, value));
}

/**
 * 更新方向 1。
 *
 * @param nextValue 新方向定义。
 */
function commitDirection1(nextValue: ExtrudeDirectionDefinition): void {
  props.context.setExtrudeDirection1?.(nextValue);
}

/**
 * 更新方向 2。
 *
 * @param nextValue 新方向定义。
 */
function commitDirection2(nextValue: ExtrudeDirectionDefinition): void {
  props.context.setExtrudeDirection2?.(nextValue);
}

/**
 * 更新薄壁特征。
 *
 * @param nextValue 新薄壁定义。
 */
function commitThinFeature(nextValue: ExtrudeThinFeatureDefinition): void {
  props.context.setExtrudeThinFeature?.(nextValue);
}

function handleDirection1EndConditionUpdate(value: string | number | null): void {
  if (!direction1.value) {
    return;
  }
  commitDirection1({
    ...direction1.value,
    endCondition: value as ExtrudeEndCondition,
  });
}

function handleDirection1ReverseUpdate(checked: boolean): void {
  if (!direction1.value) {
    return;
  }
  commitDirection1({
    ...direction1.value,
    reverse: checked,
  });
}

function handleCutFlipSideToCutUpdate(checked: boolean): void {
  props.context.setExtrudeCutOptions?.({
    ...cutOptions.value,
    flipSideToCut: checked,
  });
}

function handleDirection1DepthUpdate(value: number | null): void {
  if (!direction1.value) {
    return;
  }
  commitDirection1({
    ...direction1.value,
    depth: normalizePositiveLengthFromPropertyManager(value, direction1.value.depth),
  });
}

function toggleDirection1Draft(): void {
  if (!direction1.value) {
    return;
  }
  commitDirection1({
    ...direction1.value,
    draft: {
      ...direction1.value.draft,
      enabled: !direction1.value.draft.enabled,
    },
  });
}

function handleDirection1DraftAngleUpdate(value: number | null): void {
  if (!direction1.value) {
    return;
  }
  commitDirection1({
    ...direction1.value,
    draft: {
      ...direction1.value.draft,
      angleDeg: normalizeAngleValue(value, direction1.value.draft.angleDeg),
    },
  });
}

function handleDirection1DraftOutwardUpdate(checked: boolean): void {
  if (!direction1.value) {
    return;
  }
  commitDirection1({
    ...direction1.value,
    draft: {
      ...direction1.value.draft,
      outward: checked,
    },
  });
}

function handleDirection2EnabledUpdate(checked: boolean): void {
  if (!direction2.value) {
    return;
  }
  commitDirection2({
    ...direction2.value,
    enabled: checked,
  });
}

function handleDirection2EndConditionUpdate(value: string | number | null): void {
  if (!direction2.value) {
    return;
  }
  commitDirection2({
    ...direction2.value,
    endCondition: value as ExtrudeEndCondition,
  });
}

function handleDirection2DepthUpdate(value: number | null): void {
  if (!direction2.value) {
    return;
  }
  commitDirection2({
    ...direction2.value,
    depth: normalizePositiveLengthFromPropertyManager(value, direction2.value.depth),
  });
}

function toggleDirection2Draft(): void {
  if (!direction2.value) {
    return;
  }
  commitDirection2({
    ...direction2.value,
    draft: {
      ...direction2.value.draft,
      enabled: !direction2.value.draft.enabled,
    },
  });
}

function handleDirection2DraftAngleUpdate(value: number | null): void {
  if (!direction2.value) {
    return;
  }
  commitDirection2({
    ...direction2.value,
    draft: {
      ...direction2.value.draft,
      angleDeg: normalizeAngleValue(value, direction2.value.draft.angleDeg),
    },
  });
}

function handleDirection2DraftOutwardUpdate(checked: boolean): void {
  if (!direction2.value) {
    return;
  }
  commitDirection2({
    ...direction2.value,
    draft: {
      ...direction2.value.draft,
      outward: checked,
    },
  });
}

function handleThinFeatureEnabledUpdate(checked: boolean): void {
  if (!thinFeature.value) {
    return;
  }
  commitThinFeature({
    ...thinFeature.value,
    enabled: checked,
  });
}

function handleThinFeatureTypeUpdate(value: 'one-direction' | 'mid-plane' | 'two-direction'): void {
  if (!thinFeature.value) {
    return;
  }
  commitThinFeature({
    ...thinFeature.value,
    type: value,
  });
}

function handleThinFeatureReverseUpdate(checked: boolean): void {
  if (!thinFeature.value) {
    return;
  }
  commitThinFeature({
    ...thinFeature.value,
    reverse: checked,
  });
}

function handleThinFeatureThicknessUpdate(value: number | null): void {
  if (!thinFeature.value) {
    return;
  }
  commitThinFeature({
    ...thinFeature.value,
    thickness: normalizePositiveLengthFromPropertyManager(value, thinFeature.value.thickness),
  });
}

function handleThinFeatureThickness1Update(value: number | null): void {
  if (!thinFeature.value) {
    return;
  }
  commitThinFeature({
    ...thinFeature.value,
    thickness1: normalizePositiveLengthFromPropertyManager(value, thinFeature.value.thickness1),
  });
}

function handleThinFeatureThickness2Update(value: number | null): void {
  if (!thinFeature.value) {
    return;
  }
  commitThinFeature({
    ...thinFeature.value,
    thickness2: normalizePositiveLengthFromPropertyManager(value, thinFeature.value.thickness2),
  });
}

function handleThinFeatureCapEndsUpdate(checked: boolean): void {
  if (!thinFeature.value) {
    return;
  }
  commitThinFeature({
    ...thinFeature.value,
    capEnds: checked,
  });
}

function handleThinFeatureCapThicknessUpdate(value: number | null): void {
  if (!thinFeature.value) {
    return;
  }
  commitThinFeature({
    ...thinFeature.value,
    capThickness: normalizePositiveLengthFromPropertyManager(value, thinFeature.value.capThickness),
  });
}

function handleSelectedContoursInputActiveUpdate(active: boolean): void {
  props.context.setExtrudeSelectedContoursInputActive?.(active);
}

function handleActiveContourIndexUpdate(index: number): void {
  activeContourIndex.value = index;
}

function handleSelectedContourIndicesUpdate(indices: number[]): void {
  selectedContourIndices.value = indices;
}

function handleContourClick(payload: { item: unknown; index: number }): void {
  const item = payload.item as ExtrudeSelectedContourItem;
  props.context.highlightExtrudeSelectedContour?.(item.regionId);
}

function handleDeleteContour(payload: { item: unknown }): void {
  const item = payload.item as ExtrudeSelectedContourItem;
  props.context.removeExtrudeSelectedContour?.(item.regionId);
}

function handleDeleteAllContours(): void {
  selectedContours.value.forEach((item) => {
    props.context.removeExtrudeSelectedContour?.((item as ExtrudeSelectedContourItem).regionId);
  });
}

function handleHoverContour(regionId: string | null): void {
  props.context.highlightExtrudeSelectedContour?.(regionId);
}

watch(selectedContours, (items) => {
  if (items.length === 0) {
    activeContourIndex.value = -1;
    selectedContourIndices.value = [];
    return;
  }
  if (activeContourIndex.value >= items.length) {
    activeContourIndex.value = items.length - 1;
  }
  selectedContourIndices.value = selectedContourIndices.value.filter((index) => index >= 0 && index < items.length);
}, { deep: true });
</script>

<style scoped>
.extrude-property {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.section-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label-text {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  font-size: 12px;
  color: var(--n-text-color);
}

.field-label-empty {
  display: inline-flex;
  width: 32px;
  min-width: 32px;
  height: 20px;
}

.field-label-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
  opacity: 0.92;
}


.contour-item {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.contour-diamond {
  color: #8fb8ff;
  font-size: 12px;
}

.contour-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

</style>
